import {
  boPhanSearchColumns,
  chiPhiSearchColumns,
  dotThanhToanSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  thueSearchColumns,
  vatTu1SearchColumns,
  vatTuSearchColumns,
  viTriSearchColumns,
  vuViecSearchColumns,
  warehouseSearchColumns
} from '@/constants';
import { BoPhan, ChiPhi, DotThanhToan, HopDong, KheUoc, KhoHang, Phi, VatTu, VuViec } from '@/types/schemas';
import { CellFieldV2 as CellField, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import type { DataTableColumn } from '@/components/custom/erp/data-table-v2';
import { DvtFieldV2 } from '@/components/custom/erp/dvt-field-v2';

export const getDetailTableColumns = (
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void,
  fetchDetail: (params: MuaHangRecord) => void,
  cellError?: (rowUuid: string, field: string) => string | undefined
): (DataTableColumn & { pasteConfig?: MuaHangRecord; nonWritable?: boolean })[] => [
  {
    field: 'ma_vt',
    headerName: 'Mã sản phẩm',
    width: 200,
    renderCell: params => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTu1SearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục vật tư'
        value={params.row.ma_vt_data?.ma_vt || ''}
        onRowSelection={async (row: VatTu) => {
          const data = await fetchDetail(row?.uuid || '');
          onCellValueChange(params.row.uuid, 'ma_vt_data', data);
        }}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vt',
      resultField: 'ma_vt_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ten_vt',
    headerName: 'Tên sản phẩm',
    width: 200,
    renderCell: params => <CellField name='ten_vt' type='text' value={params.row.ma_vt_data?.ten_vt || ''} />,
    nonWritable: true
  },
  {
    field: 'dvt',
    headerName: 'Đơn vị tính',
    width: 120,
    renderCell: params => <DvtFieldV2 row={params.row} onCellValueChange={onCellValueChange} />
  },
  {
    field: 'ma_kho',
    headerName: 'Mã kho',
    width: 150,
    renderCell: params => (
      <SearchField<KhoHang>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHO_HANG}/`}
        searchColumns={warehouseSearchColumns}
        columnDisplay='ma_kho'
        dialogTitle='Danh mục kho hàng'
        value={params.row.ma_kho_data?.ma_kho}
        onRowSelection={(row: MuaHangRecord) => {
          onCellValueChange(params.row.uuid, 'ma_kho_data', row);
        }}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_kho',
      resultField: 'ma_kho_data',
      detailEndpoint: QUERY_KEYS.KHO_HANG
    }
  },
  {
    field: 'ma_vi_tri',
    headerName: 'Mã vị trí',
    width: 150,
    renderCell: params => (
      <SearchField<MuaHangRecord>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VI_TRI}/`}
        searchColumns={viTriSearchColumns}
        columnDisplay='ma_vi_tri'
        dialogTitle='Danh mục vị trí'
        value={params.row.ma_vi_tri_data?.ma_vi_tri || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_vi_tri_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vi_tri',
      resultField: 'ma_vi_tri_data',
      detailEndpoint: QUERY_KEYS.VI_TRI
    }
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 100,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='so_luong'
        type='number'
        value={params.row.so_luong || 0.0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_luong', newValue)}
      />
    )
  },
  {
    field: 'gia_nt',
    headerName: 'Giá VND',
    width: 120,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='gia_nt'
        type='number'
        value={params.row.gia_nt || 0.0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'gia_nt', newValue)}
      />
    )
  },
  {
    field: 'tien_nt',
    headerName: 'Tiền VND',
    width: 120,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='tien_nt'
        type='number'
        value={params.row.tien_nt || 0.0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
      />
    )
  },
  {
    field: 'tk_vt_data',
    headerName: 'Tk vật tư',
    width: 120,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='tk_vt'
        value={params.row.tk_vt_data?.code}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'tk_vt_data', newValue)}
      />
    )
  },
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 170,
    renderCell: params => (
      <SearchField
        searchEndpoint={`/${QUERY_KEYS.THUE}/`}
        searchColumns={thueSearchColumns}
        columnDisplay='ten_thue'
        dialogTitle='Danh mục thuế suất'
        value={params.row.ma_thue_data?.ten_thue || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_thue_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_thue',
      resultField: 'ma_thue_data',
      detailEndpoint: QUERY_KEYS.THUE
    }
  },
  {
    field: 'thue_nt',
    headerName: 'Thuế GTGT VND',
    width: 120,
    renderCell: params => (
      <CellField
        commitOnBlur
        name='thue_nt'
        type='number'
        value={params.row.thue_nt || 0.0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'thue_nt', newValue)}
      />
    )
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 150,
    renderCell: params => (
      <SearchField<BoPhan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục bộ phận'
        value={params.row.ma_bp_data?.ma_bp || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_bp')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_bp',
      resultField: 'ma_bp_data',
      detailEndpoint: QUERY_KEYS.BO_PHAN
    }
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 150,
    renderCell: params => (
      <SearchField<VuViec>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay='ma_vu_viec'
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vu_viec || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_vv')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vv',
      resultField: 'ma_vv_data',
      detailEndpoint: QUERY_KEYS.VU_VIEC
    }
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 150,
    renderCell: params => (
      <SearchField<HopDong>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
        searchColumns={hopDongSearchColumns}
        columnDisplay='ma_hd'
        dialogTitle='Danh mục hợp đồng'
        value={params.row.ma_hd_data?.ma_hd || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_hd')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_hd',
      resultField: 'ma_hd_data',
      detailEndpoint: QUERY_KEYS.HOP_DONG
    }
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 150,
    renderCell: params => (
      <SearchField<DotThanhToan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        columnDisplay='ma_dtt'
        dialogTitle='Danh mục đợt thanh toán'
        value={params.row.ma_dtt_data?.ma_dtt || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_dtt')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dtt',
      resultField: 'ma_dtt_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },
  {
    field: 'ma_ku',
    headerName: 'Khuyến mãi',
    width: 150,
    renderCell: params => (
      <SearchField<KheUoc>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        columnDisplay='ma_ku'
        dialogTitle='Danh mục khuyến mãi'
        value={params.row.ma_ku_data?.ma_ku || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_ku')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_ku',
      resultField: 'ma_ku_data',
      detailEndpoint: QUERY_KEYS.KHE_UOC
    }
  },
  {
    field: 'ma_phi',
    headerName: 'Phi',
    width: 150,
    renderCell: params => (
      <SearchField<Phi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phí'
        value={params.row.ma_phi_data?.ma_phi || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_phi')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_phi',
      resultField: 'ma_phi_data',
      detailEndpoint: QUERY_KEYS.PHI
    }
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 150,
    renderCell: params => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTuSearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục sản phẩm'
        value={params.row.ma_sp_data?.ma_vt || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_sp')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_sp',
      resultField: 'ma_sp_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 150,
    renderCell: params => (
      <SearchField
        type='text'
        searchEndpoint={`/${QUERY_KEYS.LENH_SAN_XUAT}/`}
        searchColumns={lenhSanXuatSearchColumns}
        columnDisplay='ma_lsx'
        dialogTitle='L ¯Ønh s §œn xu §t'
        value={params.row.ma_lsx_data?.ma_lsx || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_lsx')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_lsx',
      resultField: 'ma_lsx_data',
      detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
    }
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không hợp lệ',
    width: 150,
    renderCell: params => (
      <SearchField<ChiPhi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI}/`}
        searchColumns={chiPhiSearchColumns}
        columnDisplay='ma_cp'
        dialogTitle='Danh chi phí không hợp lệ'
        value={params.row.ma_cp0_data?.ma_cp || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_cp0')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_cp0',
      resultField: 'ma_cp0_data',
      detailEndpoint: QUERY_KEYS.CHI_PHI
    }
  },
  {
    field: 'so_ct_hd4',
    headerName: 'Số hoá đơn mua trong nước',
    width: 180,
    renderCell: params => params.row.id_hd?.so_ct || '',
    nonWritable: true
  },
  {
    field: 'line_hd',
    headerName: 'Dòng hóa đơn',
    width: 100,
    renderCell: params => params.row.line_hd?.line || '',
    nonWritable: true
  }
];
